import api from '@/utils/axios';
import { unwrapItem, unwrapList } from '@/utils/apiHelpers';
import type { ApiCategory, ApiNamedResource } from './types';

// ── Zones ─────────────────────────────────────────────────────
export async function listAllZones(): Promise<ApiNamedResource[]> {
  const res = await api.get('/configuration/zones/all');
  return unwrapList<ApiNamedResource>(res.data);
}

export async function listZones(): Promise<ApiNamedResource[]> {
  const res = await api.get('/configuration/zones');
  return unwrapList<ApiNamedResource>(res.data);
}

export async function createZone(name: string): Promise<ApiNamedResource> {
  const res = await api.post('/configuration/zones', { name });
  return unwrapItem<ApiNamedResource>(res.data);
}

export async function updateZone(id: number, name: string): Promise<ApiNamedResource> {
  const res = await api.patch(`/configuration/zones/${id}`, { name });
  return unwrapItem<ApiNamedResource>(res.data);
}

export async function deleteZone(id: number): Promise<void> {
  await api.delete(`/configuration/zones/${id}`);
}

// ── Categories ────────────────────────────────────────────────
export async function listAllCategories(): Promise<ApiCategory[]> {
  const res = await api.get('/configuration/categories/all');
  return unwrapList<ApiCategory>(res.data);
}

export async function listCategoriesByType(type: string): Promise<ApiCategory[]> {
  const res = await api.get(`/configuration/categories/type/${type}`);
  return unwrapList<ApiCategory>(res.data);
}

export async function createCategory(payload: { name: string; type?: string }): Promise<ApiCategory> {
  const res = await api.post('/configuration/categories', payload);
  return unwrapItem<ApiCategory>(res.data);
}

export async function updateCategory(id: number, payload: { name?: string; type?: string }): Promise<ApiCategory> {
  const res = await api.patch(`/configuration/categories/${id}`, payload);
  return unwrapItem<ApiCategory>(res.data);
}

export async function deleteCategory(id: number): Promise<void> {
  await api.delete(`/configuration/categories/${id}`);
}

// ── Account types (configuration/types) ───────────────────────
export async function listAllAccountTypes(): Promise<ApiNamedResource[]> {
  const res = await api.get('/configuration/types/all');
  return unwrapList<ApiNamedResource>(res.data);
}

export async function createAccountType(name: string): Promise<ApiNamedResource> {
  const res = await api.post('/configuration/types', { name });
  return unwrapItem<ApiNamedResource>(res.data);
}

export async function updateAccountType(id: number, name: string): Promise<ApiNamedResource> {
  const res = await api.patch(`/configuration/types/${id}`, { name });
  return unwrapItem<ApiNamedResource>(res.data);
}

export async function deleteAccountType(id: number): Promise<void> {
  await api.delete(`/configuration/types/${id}`);
}

// ── User types (configuration/users/{id}/types) ───────────────
export async function listUserTypes(userId: number): Promise<ApiNamedResource[]> {
  const res = await api.get(`/configuration/users/${userId}/types`);
  return unwrapList<ApiNamedResource>(res.data);
}

export async function syncUserTypes(userId: number, typeIds: number[]): Promise<ApiNamedResource[]> {
  const res = await api.post(`/configuration/users/${userId}/types`, { type_ids: typeIds });
  return unwrapList<ApiNamedResource>(res.data);
}

export async function detachUserType(userId: number, typeId: number): Promise<void> {
  await api.delete(`/configuration/users/${userId}/types/${typeId}`);
}

// ── User permissions ──────────────────────────────────────────
export async function listUserPermissions(userId: number): Promise<ApiNamedResource[]> {
  const res = await api.get(`/configuration/users/${userId}/permissions`);
  return unwrapList<ApiNamedResource>(res.data);
}

export async function syncUserPermissions(userId: number, permissionIds: number[]): Promise<ApiNamedResource[]> {
  const res = await api.post(`/configuration/users/${userId}/permissions`, { permission_ids: permissionIds });
  return unwrapList<ApiNamedResource>(res.data);
}

export async function detachUserPermission(userId: number, permissionId: number): Promise<void> {
  await api.delete(`/configuration/users/${userId}/permissions/${permissionId}`);
}
